import type { Channel } from 'lib-shared-types-channels';

import type {
  CustomCardHookIntegration,
  CustomCardIntegration,
} from '../../integrations';
import type { Queue } from '../../queues';
import type { Tag } from '../../tags';
import type { User } from '../../users';

import type {
  ConversationAttributes,
  ConversationCallbackAttributes,
  ConversationEmailAttributes,
  ConversationFacebookMessengerAttributes,
  ConversationMessengerAttributes,
  ConversationPstnPhoneAttributes,
  ConversationTrustpilotReviewsAttributes,
  ConversationTwitterMessengerAttributes,
  ConversationVoicemailAttributes,
  ConversationWhatsAppAttributes,
  ConversationWidgetChatAttributes,
} from './conversationAttributes.types';
import type { ConversationEvent } from './conversationEvent.types';
import type { ConversationMessageDeliveryEventComputed } from './conversationMessage.types';
import type { ConversationSummary } from './conversationSummary.types';

/**
 * Represents the direction of a conversation - i.e. from where did it
 * originate. A value of `internal` denotes that the conversation is from one
 * agent to another.
 */
export type ConversationDirection = 'inbound' | 'outbound' | 'internal';

/**
 * Represents the type of conversation link.
 *
 * - `email_forward`: Corresponds to the "send transcript" conversation.
 * - `follow_up`: Corresponds to the follow-up conversation.
 * - `side`: Corresponds to a side conversation.
 */
export type ConversationLink = 'email_forward' | 'follow_up' | 'side';

/**
 * Represents the content type for a conversation.
 *
 * - `html`: The content is a regular string with our without markup present.
 * - `text`: The content is in plaintext with or without markdown syntax present.
 */
export type ConversationContentType = 'html' | 'text';

/**
 * Represents the status of a conversation. These can be roughly be summarized
 * as follows:
 *
 * - `open`: Requires agent attention.
 * - `closed`: Does not require agent attention.
 * - `pending`: Has ended for now, but is known to open again in the future.
 * - `awaitingpending`: Similar to `pending`, but the conversation is open.
 */
export type ConversationStatus =
  | 'open'
  | 'closed'
  | 'pending'
  | 'awaitingpending';

/**
 * Represents the conversation transfer type.
 *
 * - `blind_agent`: The agent gets assigned without permission.
 * - `assisted_agent`: The agent gets assigned with permission.
 * - `internal_queue`: A regular queue transfer.
 */
export type ConversationTransferType =
  | 'blind_agent'
  | 'assisted_agent'
  | 'internal_queue';

/** Represents the main conversation. */
export type MainConversation =
  | EmailMediaMainConversation
  | InteractiveChatMediaMainConversation
  | SpeakMediaMainConversation
  | MessagingMediaMainConversation;

/** Represents the main conversation of the email media. */
export type EmailMediaMainConversation = EmailMainConversation;

/** Represents the main conversation of the messaging media. */
export type InteractiveChatMediaMainConversation =
  | WidgetChatMainConversation
  | MessengerMainConversation;

/** Represents the main conversation of the speak media. */
export type SpeakMediaMainConversation =
  | PstnPhoneMainConversation
  | CallbackMainConversation
  | VoicemailMainConversation;

/** Represents the main conversation of the interactive chat media. */
export type MessagingMediaMainConversation =
  | FacebookMessengerMainConversation
  | TwitterMainConversation
  | WhatsAppMainConversation;

/** Represents the conversation. */
export type Conversation =
  | EmailMediaConversation
  | InteractiveChatMediaConversation
  | SpeakMediaConversation
  | MessagingMediaConversation;

/** Represents the conversation of the email media. */
export type EmailMediaConversation = EmailConversation;

/** Represents the conversation of the messaging media. */
export type InteractiveChatMediaConversation =
  | WidgetChatConversation
  | MessengerConversation;

/** Represents the conversation of the speak media. */
export type SpeakMediaConversation =
  | PstnPhoneConversation
  | CallbackConversation
  | VoicemailConversation;

/** Represents the conversation of the interactive chat media. */
export type MessagingMediaConversation =
  | FacebookMessengerConversation
  | WhatsAppConversation
  | TrustpilotReviewsConversation;

/** Represents the requester conversation. */
export type RequesterConversation =
  | EmailMediaRequesterConversation
  | InteractiveChatMediaRequesterConversation
  | SpeakMediaRequesterConversation
  | MessagingMediaRequesterConversation;

/** Represents the requester conversation of the email media. */
export type EmailMediaRequesterConversation = EmailRequesterConversation;

/** Represents the requester conversation of the messaging media. */
export type InteractiveChatMediaRequesterConversation =
  | WidgetChatRequesterConversation
  | MessengerRequesterConversation;

/** Represents the requester conversation of the speak media. */
export type SpeakMediaRequesterConversation =
  | PstnPhoneRequesterConversation
  | CallbackRequesterConversation
  | VoicemailRequesterConversation;

/** Represents the requester conversation of the interactive chat media. */
export type MessagingMediaRequesterConversation =
  | FacebookMessengerRequesterConversation
  | TwitterRequesterConversation
  | WhatsAppRequesterConversation;

/**
 * Represents the excluded fields from a main conversation in a regular trimmed
 * down conversation.
 */
type ConversationExcludedFields =
  | 'events'
  | 'integrations'
  | 'async_integrations'
  | 'ratings'
  | 'last_seen'
  | 'tags'
  | 'authors';

/**
 * Represents the included fields from a main conversation in a requester
 * conversation.
 */
type RequesterConversationIncludedFields =
  | 'id'
  | 'created_at'
  | 'direction'
  | 'status'
  | 'initial_channel'
  | 'attributes';

/** Represents an async integration of a custom card. */
type AsyncIntegration = {
  card_type: CustomCardIntegration['integration_type'];
} & CustomCardHookIntegration;

/** Represents the main conversation base model. */
type MainConversationBase<
  TChannel extends Channel,
  TAttributes extends ConversationAttributes,
> = {
  /** The id of the conversation. */
  readonly id: number;
  /** The id of the organization. */
  readonly org_id: string;
  /** The agent assignment. */
  readonly assignment: ConversationAssignment | null;
  /** The history of agent assignments. */
  readonly assignments: readonly ConversationAssignment[];
  /** The queue in which the conversation resides. */
  readonly queue: Pick<Queue, 'id' | 'name' | 'is_restricted'> | null;
  /** The timestamp for when the conversation was put in a queue. */
  readonly queued_at: string | null;
  /** The attributes. */
  readonly attributes: TAttributes;
  /** The timestamp for when the conversation was closed. */
  readonly closed_at: string | null;
  /** The id of the agent who closed the conversation. */
  readonly closed_by: string | null;
  /** The timestamp for when the conversation was closed. */
  readonly created_at: string | null;
  /** The direction of the conversation. */
  readonly direction: ConversationDirection;
  /** The status of the conversation. */
  readonly status: ConversationStatus;
  /** The channel from which the conversation got created. */
  readonly initial_channel: TChannel;
  /** The id of the end-user, who requested support from the agent. */
  readonly requested_by: string;
  /** Whether or not the conversation is abandoned. */
  readonly is_abandoned: boolean | null;
  /** Whether or not a callback was consumed. */
  readonly is_callback_consumed: boolean | null;
  /** Whether or not the conversation is closed. */
  readonly is_closed: boolean;
  /** The timestamp of when the conversation got the `pending` status. */
  readonly pending_at: string | null;
  /** The id of the agent who marked the conversation as `pending`. */
  readonly pending_by: string | null;
  /** The timestamp for when the conversation transition into an `open` status. */
  readonly pending_expiration_at: string | null;
  /** The real-time information. */
  readonly live_assesment: LiveAssessment | null;
  /** The id of a linked conversation. */
  readonly linked_to: number | null;
  /** The timestamp of when the conversation link took place. */
  readonly linked_at: string | null;
  /** The type of conversation link. */
  readonly link_type: ConversationLink | null;
  /** The timestamp of when the conversation was last updated. */
  readonly updated_at: string | null;
  /** The timestamp of the latest message. */
  readonly last_message_created_at: string | null;
  /** The conversation events. */
  readonly events: readonly ConversationEvent[];
  /** The last seen (this is currently unused). */
  readonly last_seen: unknown;
  /** The integrations with outside services (this is currently unused). */
  readonly integrations: Record<string, unknown>;
  /** The async integrations with outside services. */
  readonly async_integrations: readonly AsyncIntegration[];
  /** The thumb based ratings for organizations that have not enabled csat. */
  readonly ratings: readonly ConversationThumbRating[];
  /** The tags associated with the conversation. */
  readonly tags: readonly Tag[];
  /** The authors by user id. */
  readonly authors: Record<User['id'], User>;
  /** The computed delivery status of conversation messages. */
  readonly message_delivery_status: Record<
    string,
    ConversationMessageDeliveryEventComputed
  > | null;
  /** The summary information of the conversation. */
  summary: ConversationSummaryInfo;
};

/** Represents the conversation summary information. */
export type ConversationSummaryInfo = {
  /** Whether this conversation passes the conditions for having a summary. */
  readonly can_be_generated: boolean;
  /**
   * The current summary, if an up-to-date summary already exists. Otherwise,
   * null.
   */
  readonly current: ConversationSummaryInfoSummary | null;
};

export type ConversationSummaryInfoSummary = Pick<
  ConversationSummary,
  | 'id'
  | 'summary'
  | 'created_at'
  | 'last_used_message_id'
  | 'feedback_id'
  | 'language'
>;

/** Represents the conversation transfer base model. */
type ConversationTransferBase<
  TConversationTransferType extends ConversationTransferType,
  TEntity extends Record<string, unknown>,
> = TEntity & {
  /** The id of the conversation. */
  readonly csid: number;
  /** The id of the organization. */
  readonly organization_id: string;
  /** The id of the source. */
  readonly source: string;
  /** The id of the target. */
  readonly target: string;
  /** The timestamp for when transfer was created. */
  readonly created_at: string;
  /** The type of transfer. */
  readonly transfer_type: TConversationTransferType;
};

/** Represents the conversation after work time base model. */
type ConversationAfterWorkTimeBase<
  TStatus extends string,
  TEntity extends Record<string, unknown> = Record<string, unknown>,
> = TEntity & {
  /** The id of the conversation. */
  readonly csid: number;
  /** The id of the agent. */
  readonly agent_id: string;
  /** The id of the organization. */
  readonly organization_id: string;
  /** The status. */
  readonly status: TStatus;
};

/**
 * Represents information about the agent who is expected to handle the
 * conversation.
 */
export type ConversationAgentPreference = {
  /** The full agent object. */
  readonly agent: User;
  /** The timestamp for when the agent preference expires. */
  readonly valid_until: string;
  /** The type of agent preference. */
  readonly agent_preference_type:
    | 'last_agent'
    | 'preferred_agent'
    | 'follow_up';
};

/** Represents the conversation and requester model. */
export type ConversationAndRequester = {
  /** The conversation. */
  readonly conversation: Conversation;
  /** The end-user requesting support from the agent. */
  readonly requester: User;
  /** The agent who is expected to handle the conversation. */
  readonly agent_preference?: ConversationAgentPreference | null;
};

/** Represents the conversation, requester, tags and notes model. */
export type ConversationAndRequesterAndTagsAndNotes =
  ConversationAndRequester & {
    /** The tags associated with the conversation. */
    readonly tags: readonly Tag[];
    /** The notes associated with the conversation. */
    readonly notes: readonly ConversationNote[];
  };

/** Represents the conversation history model. */
export type ConversationHistory = {
  /** The main conversation. */
  readonly main_conversation: MainConversation;
  /** The conversations this conversation links to. */
  readonly linked_conversations: readonly ConversationAndRequesterAndTagsAndNotes[];
  /** The conversation this one is linked to. */
  readonly linked_to: ConversationAndRequesterAndTagsAndNotes | null;
};

/** Represents the email main conversation model. */
export type EmailMainConversation = MainConversationBase<
  'email' | 'contact_form',
  ConversationEmailAttributes
>;

/** Represents the widget chat main conversation model. */
export type WidgetChatMainConversation = MainConversationBase<
  'widget_chat',
  ConversationWidgetChatAttributes
>;

/** Represents the messenger main conversation model. */
export type MessengerMainConversation = MainConversationBase<
  'messenger',
  ConversationMessengerAttributes
>;

/** Represents the facebook messenger main conversation model. */
export type FacebookMessengerMainConversation = MainConversationBase<
  'facebook_messenger',
  ConversationFacebookMessengerAttributes
>;

/** Represents the twitter main conversation model. */
export type TwitterMainConversation = MainConversationBase<
  'twitter',
  ConversationTwitterMessengerAttributes
>;

/** Represents the whats-app main conversation model. */
export type WhatsAppMainConversation = MainConversationBase<
  'whats_app',
  ConversationWhatsAppAttributes
>;

/** Represents the pstn-phone main conversation model. */
export type PstnPhoneMainConversation = MainConversationBase<
  'pstn_phone',
  ConversationPstnPhoneAttributes
>;

/** Represents the trustpilot reviews main conversation model. */
export type TrustpilotReviewsMainConversation = MainConversationBase<
  'trustpilotreviews',
  ConversationTrustpilotReviewsAttributes
>;

/** Represents the pstn-phone main conversation model. */
export type CallbackMainConversation = MainConversationBase<
  'callback',
  ConversationCallbackAttributes
>;

/** Represents the voicemail main conversation model. */
export type VoicemailMainConversation = MainConversationBase<
  'voicemail',
  ConversationVoicemailAttributes
>;

/** Represents the email conversation model. */
export type EmailConversation = Omit<
  EmailMainConversation,
  ConversationExcludedFields
>;

/** Represents the email conversation model. */
export type WidgetChatConversation = Omit<
  WidgetChatMainConversation,
  ConversationExcludedFields
>;

/** Represents the email conversation model. */
export type MessengerConversation = Omit<
  MessengerMainConversation,
  ConversationExcludedFields
>;

/** Represents the email conversation model. */
export type PstnPhoneConversation = Omit<
  PstnPhoneMainConversation,
  ConversationExcludedFields
>;

/** Represents the callback conversation model. */
export type CallbackConversation = Omit<
  CallbackMainConversation,
  ConversationExcludedFields
>;

/** Represents the voicemail conversation model. */
export type VoicemailConversation = Omit<
  VoicemailMainConversation,
  ConversationExcludedFields
>;

/** Represents the facebook conversation model. */
export type FacebookMessengerConversation = Omit<
  FacebookMessengerMainConversation,
  ConversationExcludedFields
>;

/** Represents the twitter conversation model. */
export type TwitterConversation = Omit<
  TwitterMainConversation,
  ConversationExcludedFields
>;

/** Represents the whats-app conversation model. */
export type WhatsAppConversation = Omit<
  WhatsAppMainConversation,
  ConversationExcludedFields
>;

/** Represents the trustpilot reviews conversation model. */
export type TrustpilotReviewsConversation = Omit<
  TrustpilotReviewsMainConversation,
  ConversationExcludedFields
>;

/** Represents the email requester conversation model. */
export type EmailRequesterConversation = Pick<
  EmailMainConversation,
  RequesterConversationIncludedFields
>;

/** Represents the widget chat requester conversation model. */
export type WidgetChatRequesterConversation = Pick<
  WidgetChatMainConversation,
  RequesterConversationIncludedFields
>;

/** Represents the messenger requester conversation model. */
export type MessengerRequesterConversation = Pick<
  MessengerMainConversation,
  RequesterConversationIncludedFields
>;

/** Represents the pstn phone requester conversation model. */
export type PstnPhoneRequesterConversation = Pick<
  PstnPhoneMainConversation,
  RequesterConversationIncludedFields
>;

/** Represents the callback requester conversation model. */
export type CallbackRequesterConversation = Pick<
  CallbackMainConversation,
  RequesterConversationIncludedFields
>;

/** Represents the voicemail requester conversation model. */
export type VoicemailRequesterConversation = Pick<
  VoicemailMainConversation,
  RequesterConversationIncludedFields
>;

/** Represents the Facebook messenger requester conversation model. */
export type FacebookMessengerRequesterConversation = Pick<
  FacebookMessengerMainConversation,
  RequesterConversationIncludedFields
>;

/** Represents the twitter requester conversation model. */
export type TwitterRequesterConversation = Pick<
  TwitterMainConversation,
  RequesterConversationIncludedFields
>;

/** Represents the WhatsApp requester conversation model. */
export type WhatsAppRequesterConversation = Pick<
  WhatsAppMainConversation,
  RequesterConversationIncludedFields
>;

/** Represents the agent assignment model. */
export type ConversationAssignment = {
  /** The agent id. */
  readonly agent_id: string;
  /** The agent. */
  readonly agent: User | null;
  /** The timestamp for when the agent got assigned. */
  readonly assigned_at: string;
  /** The timestamp for when the agent got unassigned. */
  readonly unassigned_at: string | null;
};

/** Represents the live-assessments model. */
export type LiveAssessment = {
  /** Whether or not the end-user is connected and thereby live. */
  readonly is_end_user_connected: boolean;
  /** The timestamp for when the conversation became live. */
  readonly timestamp: string;
};

/** Represents the conversation note model. */
export type ConversationNote = {
  /** The id of the note. */
  readonly id: number;
  /** The id of the author. */
  readonly author_id: string;
  /** The id of the conversation the note belongs to. */
  readonly csid: number;
  /** The id of the organization. */
  readonly organization_id: string;
  /** The timestamp for when the note was created. */
  readonly created_at: string;
  /** The text. */
  readonly message: string;
  /** The author of the note. */
  readonly author: User;
};

/** The available the thumbs up/down scores. */
export enum ConversationThumbScore {
  ThumbsDown = 0,
  ThumbsUp = 1,
}

/** Represents the conversation thumb rating model. */
export type ConversationThumbRating = {
  /** The id of the rating. */
  readonly id: string;
  /** The timestamp for when the rating occurred. */
  readonly created_at: string;
  /** The type of rating. */
  readonly type: 'thumpsupordown';
  /** The score. */
  readonly score: ConversationThumbScore;
  /** The message. */
  readonly message: string | null;
};

/** Represents the available bulk conversations validation errors. */
export type ConversationsIdsBulkValidationError =
  | 'PHONE_CONVERSATION_NOT_SUPPORTED'
  | 'FACEBOOK_MESSENGER_OUTBOUND_EXPIRED'
  | 'WHATSAPP_OUTBOUND_EXPIRED'
  | 'VOICEMAIL_CONVERSATION_NOT_SUPPORTED'
  | 'CALLBACK_CONVERSATION_NOT_SUPPORTED';

/** Represents the available bulk conversations validation types. */
export type ConversationsIdsBulkValidationType = 'SEND_REPLY';

/** Represents the failed validation for bulk conversations. */
export type ConversationsIdsBulkFailedValidation = {
  /** The conversation ids. */
  readonly csids: readonly number[];
  /** The validation error. */
  readonly error: ConversationsIdsBulkValidationError;
  /** The type of validation. */
  readonly validation_type: ConversationsIdsBulkValidationType;
};

/** Represents the conversations id bulk. */
export type ConversationsIdsBulk = {
  /** The conversation ids. */
  readonly csids: readonly number[];
  /** The total number of conversations. */
  readonly total_conversations: number;
  /** The validation errors. */
  readonly validations_failed: readonly ConversationsIdsBulkFailedValidation[];
};

/** Represents the type of a conversation bulk action. */
export type ConversationBulkActionType =
  | 'add_internal_note'
  | 'add_tag'
  | 'cancel_csat'
  | 'claim'
  | 'close'
  | 'remove_tag'
  | 'send_reply'
  | 'transfer_to_queue'
  | 'link';

/** Represents the conversation bulk action. */
export type ConversationBulkAction = {
  /** The type of action. */
  action_type: ConversationBulkActionType;
  /** The id of the agent who performed the action. */
  agent_id: string;
  /** The id of the bulk action. */
  bulk_id: string;
  /** The ids of the conversations the bulk action is performed on. */
  conversation_ids: number[];
  /** The id of the organization. */
  organization_id: string;
  /** The request input. */
  request_input: string;
  /** The status of the bulk action. */
  status: string;
};

/** Represents the failed conversation bulk action model. */
export type ConversationBulkFailedAction = {
  /** The type of action. */
  action_type: ConversationBulkActionType;
  /** The id of the agent who performed the action. */
  agent_id: string;
  /** The id of the bulk the action is part of. */
  bulk_id: string;
  /** The id of the conversation. */
  conversation_id: number;
  /** The error message. */
  error_message: string;
  /** The id of the organization. */
  organization_id: string;
  /** The timestamp for when the action failed. */
  timestamp: string;
};

/** Represents the requester conversations model. */
export type RequesterConversations = {
  /** The requester conversations. */
  readonly conversations: readonly RequesterConversation[];
};

/** Represents the scheduled conversation batches model. */
export type ScheduledConversationBatches = {
  /** The batches. */
  readonly batches: readonly ScheduledConversationBatch[];
};

/** Represents the scheduled conversation batch model. */
export type ScheduledConversationBatch = {
  /** The id of the batch. */
  readonly batch_id: string;
  /** The name of the batch. */
  readonly name: string;
  /** The conversation channel. */
  readonly channel: Extract<Channel, 'callback'>;
  /** The status. */
  readonly status: 'scheduled';
  /** The url to the csv file. */
  readonly csv_url: string;
  /** The id of the queue. */
  readonly queue_id: string;
  /** The name of the queue. */
  readonly queue_name: string;
  /** The id of the agent who scheduled the conversations. */
  readonly scheduled_by: string | null;
};

/** Represents the conversation transfer model. */
export type ConversationTransfer = ConversationTransferBase<
  'blind_agent' | 'assisted_agent' | 'internal_queue',
  {
    /** The name of the transferred target. */
    readonly target_name: string;
  }
>;

/** Represents the conversation transfer answer model. */
export type ConversationTransferAnswer = ConversationTransferBase<
  'blind_agent' | 'assisted_agent',
  {
    /** Whether or not the agent accepted. */
    readonly accepted: boolean;
    /** The timestamp for when the transfer was answered. */
    readonly answered_at: string;
    /** The timestamp for when the transfer was resolved. */
    readonly resolved_at: string;
  }
>;

/** Represents the conversation after work time complete model. */
export type ConversationAfterWorkTimeComplete =
  ConversationAfterWorkTimeBase<'completed'>;

/** Represents the conversation after work time extend model. */
export type ConversationAfterWorkTimeExtend = ConversationAfterWorkTimeBase<
  'waiting',
  {
    /** The remaining wait time in seconds. */
    readonly remaining_duration_in_seconds: number;
  }
>;

/** Represents a contact point participant in the conversation. */
export type ContactPoint = {
  /**
   * Used to distinguish between different types of contact points, it's an
   * upper layer abstraction to channels, for example telephony and sms would
   * both use PHONE_NUMBER as contact point kind.
   */
  readonly kind: string;
  /**
   * The actual identifier of the contact point, i.e: for email it would be the
   * address 'john.doe@example.com'
   */
  readonly value: string;
  /**
   * Extra attributes that could be attached to such contact point, i.e for
   * twitter we use the 'screen_name' also known as handle: '@dixahq'
   */
  readonly attributes: Record<string, string>;
};
